// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css } from '@emotion/css';
import { Typography, Skeleton, Tooltip, Theme, useTheme } from '@mui/material';
import { BiInfoCircle } from 'react-icons/bi';

// TYPES ---------------------------------------------------------------- //

export type MetricCardTone = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';

interface MetricCardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  /** Plain-language definition of how the metric is calculated, shown on hover */
  tooltip?: string;
  tone?: MetricCardTone;
  loading?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const MetricCard = ({ label, value, subtitle, tooltip, tone = 'default', loading = false }: MetricCardProps) => {
  const theme = useTheme();
  const cls = useStyles(theme, tone);

  return (
    <div className={cls.card}>
      <div className={cls.labelRow}>
        <Typography className={cls.label}>{label}</Typography>
        {tooltip ? (
          <Tooltip title={tooltip} placement='top' arrow>
            <span className={cls.infoIcon}>
              <BiInfoCircle size={14} />
            </span>
          </Tooltip>
        ) : null}
      </div>

      {loading ? (
        <Skeleton variant='text' width={80} height={40} />
      ) : (
        <Typography className={cls.value}>{value}</Typography>
      )}

      {subtitle ? <Typography className={cls.subtitle}>{subtitle}</Typography> : null}
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme, tone?: MetricCardTone) => {
  const valueColor =
    tone && tone !== 'default' ? theme?.palette?.[tone]?.main : theme?.palette?.text?.primary;

  return {
    card: css`
      position: relative;
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: ${theme?.spacing(2)};
      border: 1px solid ${theme?.palette?.divider};
      border-radius: 10px;
      background-color: ${theme?.palette?.background?.paper};
    `,
    labelRow: css`
      display: flex;
      align-items: center;
      gap: ${theme?.spacing(0.5)};
    `,
    label: css`
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-size: 11px !important;
      font-weight: 600 !important;
      color: ${theme?.palette?.text?.secondary};
    `,
    infoIcon: css`
      display: inline-flex;
      align-items: center;
      color: ${theme?.palette?.text?.disabled};
      cursor: help;
    `,
    value: css`
      margin-top: ${theme?.spacing(0.5)} !important;
      font-size: 28px !important;
      font-weight: 700 !important;
      line-height: 1.2 !important;
      color: ${valueColor};
    `,
    subtitle: css`
      margin-top: ${theme?.spacing(0.5)} !important;
      font-size: 12px !important;
      color: ${theme?.palette?.text?.disabled};
    `,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default MetricCard;
